import React, { useEffect, useMemo } from 'react';
import ReactStars from 'react-rating-stars-component';
import { connect } from 'react-redux';
import styled from 'styled-components';

import Loader from '../../../Components/UI/Loader/Loader';
import { getBestArtisanAPI } from '../../../Store/Actions/userActions';
import { useI18n } from '../../../i18n/I18nProvider';

const rank = (artisans, field) => [...artisans]
  .filter((artisan) => Number(artisan?.[field]) > 0)
  .sort((a, b) => Number(b?.[field] || 0) - Number(a?.[field] || 0))
  .slice(0, 3);

const ArtisanEntry = ({ artisan, showJobs = false }) => {
  const { t } = useI18n();
  const name = artisan.firstName || t('common.professional');
  const jobCount = Number(artisan.total_num_jobs) || 0;
  return (
    <ArtisanRow>
      <ProfileWrap>
        <Avatar
          src={artisan.profile_image || '/images/user.svg'}
          alt={artisan.firstName ? t('rankings.profileAlt', { name: artisan.firstName }) : t('rankings.professionalAlt')}
        />
        <ProfileInfo>
          <ArtisanName>{name}</ArtisanName>
          {showJobs && <JobBadge>{t('rankings.jobs', { count: jobCount })}</JobBadge>}
          <RatingRow>
            <ReactStars
              count={5}
              value={Number(artisan.artisan_rating) || 0}
              size={14}
              isHalf
              edit={false}
              activeColor="#c0a375"
            />
            <RatingValue>{(Number(artisan.artisan_rating) || 0).toFixed(1)}</RatingValue>
          </RatingRow>
        </ProfileInfo>
      </ProfileWrap>
    </ArtisanRow>
  );
};

const RankingCard = ({ title, subtitle, artisans, showJobs }) => {
  const { t } = useI18n();
  return (
    <SectionCard>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardSubtitle>{subtitle}</CardSubtitle>
      </CardHeader>
      <FeedList>
        {artisans.length > 0 ? artisans.map((artisan) => (
          <ArtisanEntry key={artisan.user_id} artisan={artisan} showJobs={showJobs} />
        )) : (
          <EmptyState>{t('rankings.empty')}</EmptyState>
        )}
      </FeedList>
    </SectionCard>
  );
};

const RightSection = ({ artisans = [], loading, error, getArtisans }) => {
  const { t } = useI18n();

  useEffect(() => {
    const unsubscribe = getArtisans();
    return typeof unsubscribe === 'function' ? unsubscribe : undefined;
  }, [getArtisans]);

  const bestRated = useMemo(() => rank(artisans, 'artisan_rating'), [artisans]);
  const mostActive = useMemo(() => rank(artisans, 'total_num_jobs'), [artisans]);

  return (
    <Container>
      {loading && <Loader />}
      {error && <ErrorMessage>{error}</ErrorMessage>}
      <RankingCard
        title={t('rankings.bestTitle')}
        subtitle={t('rankings.bestSubtitle')}
        artisans={bestRated}
      />
      <RankingCard
        title={t('rankings.activeTitle')}
        subtitle={t('rankings.activeSubtitle')}
        artisans={mostActive}
        showJobs
      />
    </Container>
  );
};

const Container = styled.aside`
  display: grid;
  gap: 1rem;
  align-self: start;
  @media (max-width: 768px) { display: none; }
`;
const SectionCard = styled.section`padding: 1rem; overflow: hidden; border: 1px solid #e9edf3; border-radius: 1.1rem; background: #fff; box-shadow: 0 10px 24px rgba(23, 32, 51, 0.06);`;
const CardHeader = styled.header`padding-bottom: .75rem; border-bottom: 1px solid #eef2f7;`;
const CardTitle = styled.h3`margin: 0; color: #172033; font-size: 1rem; font-weight: 800;`;
const CardSubtitle = styled.p`margin: .2rem 0 0; color: #758097; font-size: .84rem; line-height: 1.5;`;
const FeedList = styled.div`display: grid; gap: .65rem; margin-top: .75rem;`;
const ArtisanRow = styled.article`padding: .75rem; border: 1px solid #edf1f6; border-radius: .9rem; background: #f9fbfe;`;
const ProfileWrap = styled.div`display: flex; align-items: center; gap: .75rem; min-width: 0;`;
const Avatar = styled.img`width: 3.1rem; height: 3.1rem; flex: 0 0 auto; border: 2px solid #edf2f7; border-radius: 50%; object-fit: cover;`;
const ProfileInfo = styled.div`display: grid; gap: .3rem; min-width: 0;`;
const ArtisanName = styled.strong`overflow: hidden; color: #172033; font-size: .95rem; text-overflow: ellipsis; white-space: nowrap;`;
const RatingRow = styled.div`display: flex; flex-wrap: wrap; align-items: center; gap: .45rem;`;
const RatingValue = styled.span`color: #6d7687; font-size: .8rem; font-weight: 700;`;
const JobBadge = styled.span`width: fit-content; padding: .22rem .55rem; border-radius: 999px; background: #eef4ff; color: #1246a5; font-size: .76rem; font-weight: 800;`;
const EmptyState = styled.p`margin: 0; padding: .85rem; border-radius: .8rem; background: #f9fbfe; color: #6f788b; font-size: .88rem; line-height: 1.5;`;
const ErrorMessage = styled.p`margin: 0; padding: .8rem; border: 1px solid #efcfcb; border-radius: .8rem; background: #fff6f5; color: #8c332c; font-size: .86rem;`;

const mapStateToProps = ({ artisans }) => ({
  artisans: artisans?.artisans || [],
  loading: Boolean(artisans?.loading),
  error: artisans?.error,
});
const mapDispatchToProps = (dispatch) => ({
  getArtisans: () => dispatch(getBestArtisanAPI()),
});

export default connect(mapStateToProps, mapDispatchToProps)(RightSection);
